import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, authWithGoogle } from '../api/appApi';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import axios from 'axios';
import { useGoogleLogin } from "@react-oauth/google";
import CONFIG from '../config/constants';

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = Yup.object({
    email: Yup.string().email('Invalid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    try {
      const res = await login(values.email, values.password);
      if (res?.success && res?.data?.token) {
        localStorage.setItem('access_token', res?.data?.token);
        Swal.fire({ icon: 'success', title: 'Login Successful', timer: 1500, showConfirmButton: false });
        navigate('/');
      } else {
        Swal.fire({ icon: 'error', title: 'Login Failed', text: res.message || 'Invalid credentials.' });
      }
      setLoading(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Login Failed', text: err.response?.data?.message || 'Login failed.' });
      setLoading(false);
    }
    setSubmitting(false);
  };

  const loginWithGoogle = useGoogleLogin({
    flow: "auth-code",
    onSuccess: async (tokenResponse) => {
      setLoading(true);
      try {
        const res = await axios.post("https://oauth2.googleapis.com/token", {
          code: tokenResponse.code,
          client_id: CONFIG.GOOGLE_OAUTH_CLIENT_ID,
          client_secret: CONFIG.GOOGLE_OAUTH_CLIENT_SECRET,
          redirect_uri: CONFIG.WEB_URL,
          grant_type: "authorization_code",
        });

        const responseFromGoogle = res.data;
        if (responseFromGoogle.id_token) {
          const result = await authWithGoogle(responseFromGoogle.id_token);
          if (result?.success && result?.data?.token) {
            localStorage.setItem('access_token', result?.data?.token);
            Swal.fire({ icon: 'success', title: 'Login Successful', timer: 1500, showConfirmButton: false });
            navigate('/');
          } else {
            Swal.fire({ icon: 'error', title: 'Login Failed', text: result.message || 'Error with authentication from Google.' });
          }
        } else {
          Swal.fire({ icon: 'error', title: 'Login Failed', text: 'Error with authentication from Google.' });
        }
        setLoading(false);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Login Failed', text: err.response?.data?.message || 'Error with authentication from Google.' });
        setLoading(false);
      }
    },
    onError: (err) => {
      Swal.fire({ icon: 'error', title: 'Login Failed', text: err?.message || 'Error with authentication from Google.' });
    },
  });

  return (
    <div className="row min-vh-100 g-0">
      <div className="col-md-6 d-none d-md-flex align-items-center justify-content-center">
        <video
            autoPlay
            loop
            muted
            playsInline
            controls={false}
            disablePictureInPicture
            controlsList="nodownload nofullscreen noremoteplayback"
            className="w-100 h-100 object-fit-cover"
            onContextMenu={(e) => e.preventDefault()}
          >
            <source src="/banner_video.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
      </div>
      <div className="col-12 col-md-6 d-flex align-items-center justify-content-center">
        <Formik
          initialValues={{ email: '', password: '' }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="w-80 w-sm-50">
              <h1 className="mb-4 welcome-title text-center">WELCOME TO THE LICHIC</h1>
              <h2 className="mb-4 form-title text-center">SIGN IN</h2>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email</label>
                <Field type="email" name="email" className={`form-control${errors.email && touched.email ? ' is-invalid' : ''}`} id="email" placeholder="Enter Email ID" />
                <div className="form-text text-danger"><ErrorMessage name="email" /></div>
              </div>
              <div className="mb-3 position-relative">
                <div className="d-flex justify-content-between align-items-center">
                  <label htmlFor="password" className="form-label">Password</label>
                  <a href="/forgot-password" className="forgot-link">Forgot Password?</a>
                </div>
                <div style={{ position: 'relative' }}>
                  <Field
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className={`form-control${errors.password && touched.password ? ' is-invalid' : ''}`}
                    id="password"
                    placeholder="Enter Password"
                  />
                  <span
                    className="eye-icon"
                    onClick={() => setShowPassword((prev) => !prev)}
                    tabIndex={0}
                    role="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    { showPassword ? 
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.1679 1.13964C14.3567 0.95083 14.6694 0.95101 14.8584 1.13964C15.0473 1.32854 15.0473 1.64117 14.8584 1.83007L9.99312 6.69628L6.70113 9.98632C6.69953 9.98798 6.69886 9.99054 6.69722 9.99218L1.83101 14.8584C1.7333 14.9561 1.60908 15.0019 1.48531 15.0019C1.3617 15.0019 1.23821 14.9559 1.14058 14.8584C0.951674 14.6695 0.951674 14.3569 1.14058 14.168L3.33883 11.9697C2.7111 11.4056 2.14382 10.7292 1.65426 9.95312C0.963794 8.87839 0.963869 7.12623 1.65426 6.04491C3.24367 3.55668 5.55623 2.1231 7.99898 2.12304C9.35984 2.12304 10.7027 2.57049 11.8945 3.41308L14.1679 1.13964ZM12.9306 4.96972C13.1391 4.80036 13.4452 4.82703 13.6211 5.04199C13.8751 5.35462 14.1232 5.69321 14.3447 6.04491C15.035 7.11972 15.0351 8.87191 14.3447 9.95312C12.7553 12.4413 10.4427 13.8739 7.99996 13.874C7.13374 13.874 6.28674 13.6986 5.47262 13.3535C5.22508 13.2493 5.10765 12.9624 5.21187 12.7148C5.31606 12.4675 5.60216 12.3502 5.84957 12.4541C6.54005 12.7472 7.26356 12.8974 7.99312 12.8975C10.0971 12.8975 12.1105 11.6339 13.5175 9.42577C14.0061 8.6637 14.006 7.3344 13.5175 6.57226C13.3156 6.25308 13.0938 5.94676 12.8593 5.66015C12.6901 5.45174 12.7223 5.14559 12.9306 4.96972ZM7.99898 3.10058C5.89505 3.10064 3.88256 4.36428 2.47555 6.57226C1.98698 7.33438 1.98698 8.66365 2.47555 9.42577C2.93382 10.1406 3.45566 10.763 4.02926 11.2783L5.69234 9.61523C5.35978 9.14316 5.17871 8.58382 5.17867 7.99902C5.17867 6.44233 6.44226 5.1789 7.99898 5.1787C8.58388 5.1787 9.14404 5.35882 9.61617 5.6914L11.1923 4.11523C10.201 3.44895 9.10284 3.10058 7.99898 3.10058ZM10.3769 7.97949C10.644 8.03159 10.8196 8.2857 10.7675 8.55273C10.5591 9.67962 9.66043 10.5718 8.54 10.7803C8.5076 10.7738 8.48156 10.7802 8.44918 10.7803C8.22133 10.7803 8.01254 10.618 7.96676 10.3838C7.91466 10.1168 8.09044 9.86211 8.35738 9.8164C9.07394 9.68612 9.67339 9.08663 9.80367 8.37011C9.85593 8.10331 10.11 7.93401 10.3769 7.97949ZM7.99898 6.15527C6.98294 6.15546 6.15621 6.98298 6.15621 7.99902C6.15624 8.32464 6.24014 8.6376 6.39644 8.91113L8.91109 6.39648C8.63753 6.2402 8.32463 6.15527 7.99898 6.15527Z" fill="#6C7982"/>
                      </svg>
                    : <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 10.507C7.50325 10.507 7.01765 10.3597 6.60461 10.0837C6.19158 9.80771 5.86966 9.41545 5.67956 8.95651C5.48946 8.49757 5.43972 7.99256 5.53663 7.50535C5.63354 7.01815 5.87275 6.57062 6.22401 6.21936C6.57527 5.8681 7.0228 5.62889 7.51001 5.53198C7.99721 5.43507 8.50222 5.48481 8.96116 5.67491C9.4201 5.86501 9.81236 6.18693 10.0883 6.59996C10.3643 7.013 10.5116 7.4986 10.5116 7.99535C10.5098 8.66091 10.2446 9.29868 9.77395 9.7693C9.30333 10.2399 8.66556 10.5051 8 10.507ZM8 6.53023C7.71023 6.53023 7.42696 6.61616 7.18602 6.77715C6.94509 6.93814 6.7573 7.16696 6.64641 7.43467C6.53552 7.70239 6.5065 7.99697 6.56304 8.28118C6.61957 8.56538 6.75911 8.82644 6.96401 9.03134C7.16891 9.23624 7.42996 9.37578 7.71417 9.43231C7.99837 9.48885 8.29296 9.45983 8.56068 9.34894C8.82839 9.23805 9.05721 9.05026 9.2182 8.80932C9.37919 8.56839 9.46512 8.28512 9.46512 7.99535C9.46512 7.60678 9.31076 7.23412 9.03599 6.95935C8.76123 6.68459 8.38857 6.53023 8 6.53023V6.53023ZM8 14.4907C6.1094 14.4041 4.31138 13.6473 2.92791 12.3558C1.40698 11.0233 0.5 9.3907 0.5 7.99535C0.5 4.89767 4.42791 1.5 8 1.5C11.5721 1.5 15.5 4.98837 15.5 7.99535C15.5 11.1488 11.6419 14.4907 8 14.4907ZM8 2.54651C5.04884 2.54651 1.54651 5.49767 1.54651 7.99535C1.54651 9.08372 2.34186 10.4512 3.63953 11.5674C4.83059 12.681 6.37251 13.3446 8 13.4442C11.3209 13.4442 14.4535 10.3744 14.4535 7.99535C14.4535 5.74186 11.1186 2.54651 8 2.54651Z" fill="#6C7982"/>
                      </svg>
                    }
                  </span>
                </div>
                <div className="form-text text-danger"><ErrorMessage name="password" /></div>
              </div>
              <button type="submit" className="btn btn-primary w-100 submit-btn" disabled={isSubmitting}>Sign In</button>
              <div className="my-3 text-center or-divider d-flex justify-content-center align-items-center">
                <hr className="hr-line" style={{ "margin-right": "10px" }} />
                <span style={{ color: "#566067" }}>OR</span>
                <hr className="hr-line" style={{ "margin-left": "10px" }} />
              </div>
              <button type="button" className="btn btn-outline-danger w-100 mb-2 d-flex justify-content-center align-items-center google-btn" onClick={loginWithGoogle}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><defs><radialGradient id="prefix__b" cx="1.479" cy="12.788" fx="1.479" fy="12.788" r="9.655" gradientTransform="matrix(.8032 0 0 1.0842 2.459 -.293)" gradientUnits="userSpaceOnUse"><stop offset=".368" stop-color="#ffcf09"/><stop offset=".718" stop-color="#ffcf09" stop-opacity=".7"/><stop offset="1" stop-color="#ffcf09" stop-opacity="0"/></radialGradient><radialGradient id="prefix__c" cx="14.295" cy="23.291" fx="14.295" fy="23.291" r="11.878" gradientTransform="matrix(1.3272 0 0 1.0073 -3.434 -.672)" gradientUnits="userSpaceOnUse"><stop offset=".383" stop-color="#34a853"/><stop offset=".706" stop-color="#34a853" stop-opacity=".7"/><stop offset="1" stop-color="#34a853" stop-opacity="0"/></radialGradient><linearGradient id="prefix__d" x1="23.558" y1="6.286" x2="12.148" y2="20.299" gradientUnits="userSpaceOnUse"><stop offset=".671" stop-color="#4285f4"/><stop offset=".885" stop-color="#4285f4" stop-opacity="0"/></linearGradient><clipPath id="prefix__a"><path d="M22.36 10H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53h-.013l.013-.01c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09c.87-2.6 3.3-4.53 6.16-4.53 1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07 1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93v.01C3.99 20.53 7.7 23 12 23c2.97 0 5.46-.98 7.28-2.66 2.08-1.92 3.28-4.74 3.28-8.09 0-.78-.07-1.53-.2-2.25z" fill="none"/></clipPath></defs><path d="M22.36 10H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53h-.013l.013-.01c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09c.87-2.6 3.3-4.53 6.16-4.53 1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07 1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93v.01C3.99 20.53 7.7 23 12 23c2.97 0 5.46-.98 7.28-2.66 2.08-1.92 3.28-4.74 3.28-8.09 0-.78-.07-1.53-.2-2.25z" fill="#fc4c53"/><g clip-path="url(#prefix__a)"><ellipse cx="3.646" cy="13.572" rx="7.755" ry="10.469" fill="url(#prefix__b)"/><ellipse cx="15.538" cy="22.789" rx="15.765" ry="11.965" transform="rotate(-7.12 15.539 22.789)" fill="url(#prefix__c)"/><path fill="url(#prefix__d)" d="M11.105 8.28l.491 5.596.623 3.747 7.362 6.848 8.607-15.897-17.083-.294z"/></g></svg>
                <span>Sign in with Google</span>
              </button>
              {/* <GoogleLogin
                onSuccess={(credentialResponse) => {
                  const decoded = jwtDecode(credentialResponse.credential);
                  console.log("Decoded User Info:", decoded);
                }}
                onError={() => {
                  console.log("Login Failed");
                }}
              /> */}
              <div className="mt-3 text-center">
                <label className="form-label">Don't have an account? <a href="/signup">Sign Up</a></label>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {loading && <div className="page-loader"><span className="spinner-border" /></div>}
    </div>
  );
};

export default Login;
